import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useChatProposal,
  useConfirmChatProposal,
  usePatchChatProposal,
  useRejectChatProposal,
  useUndoChatProposal,
} from '@/hooks/useChatActions';
import { cn } from '@/utils/cn';
import type { ChatActionProposal } from '@/types/chat';

function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('state' in o && o.state != null) return String(o.state);
    if ('budget' in o && o.budget != null) return String(o.budget);
    if ('bid' in o && o.bid != null) return String(o.bid);
    if ('keywordText' in o) {
      const mt = o.matchType ? ` (${String(o.matchType)})` : '';
      const bid = o.bid != null ? ` @ ${String(o.bid)}` : '';
      return `${String(o.keywordText)}${mt}${bid}`;
    }
    if ('decision' in o) return String(o.decision);
    if ('schedule' in o) {
      if (o.schedule == null) return 'clear schedule';
      if (typeof o.schedule === 'object') {
        const s = o.schedule as Record<string, unknown>;
        if (s.exists === false || s.mode === 'clear') return 'clear schedule';
        const windows = Array.isArray(s.time_windows) ? s.time_windows.length : 0;
        return windows ? `schedule (${windows} windows)` : 'update schedule';
      }
    }
    if ('mode' in o && (o.mode === 'capture' || o.mode === 'restore')) {
      return String(o.mode);
    }
    return JSON.stringify(v);
  }
  return String(v);
}

function impactCallout(proposal: ChatActionProposal): string | null {
  const summary = proposal.summary || '';
  if (/impact callout/i.test(summary)) {
    const match = summary.match(/Impact callout:[^.]+/i);
    return match ? match[0] : summary;
  }
  const params = proposal.params || {};
  const mode = String(params.mode || '');
  const value = Number(params.value);
  if (
    (proposal.action_type === 'campaign_budget_bulk' ||
      proposal.action_type === 'keyword_bid_bulk') &&
    mode === 'percent' &&
    Number.isFinite(value) &&
    Math.abs(value) >= 25
  ) {
    return `Impact callout: ${value}% change is high.`;
  }
  if (proposal.action_type === 'bid_snapshot_restore') {
    return 'Impact callout: restoring a snapshot can rewrite many bids.';
  }
  if (
    proposal.action_type === 'recommendation_apply_pack' &&
    (proposal.params?.partial_undo || proposal.is_undo)
  ) {
    return 'Partial undo: only reversible recommendation items are included.';
  }
  return null;
}

type Props = {
  profileId: string;
  threadId: string;
  proposalId: string;
  onOpenProposal?: (proposal: ChatActionProposal) => void;
};

export function ActionProposalCard({
  profileId,
  threadId,
  proposalId,
  onOpenProposal,
}: Props) {
  const { data: proposal, isLoading, error, refetch } = useChatProposal(
    profileId,
    threadId,
    proposalId
  );
  const confirmMut = useConfirmChatProposal(profileId);
  const rejectMut = useRejectChatProposal(profileId);
  const patchMut = usePatchChatProposal(profileId);
  const undoMut = useUndoChatProposal(profileId);
  const [localError, setLocalError] = useState<string | null>(null);

  const editable = proposal?.status === 'draft' || proposal?.status === 'undo_draft';
  const busy =
    confirmMut.isPending ||
    rejectMut.isPending ||
    patchMut.isPending ||
    undoMut.isPending;

  const selectedCount = useMemo(
    () => (proposal?.items ?? []).filter((i) => i.selected !== false).length,
    [proposal?.items]
  );

  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-foreground-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading proposal…
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="mt-2 rounded-xl border border-error/20 bg-error-subtle px-4 py-3 text-sm text-error-ink">
        Could not load action proposal.
      </div>
    );
  }

  const toggleItem = async (itemId: string, selected: boolean) => {
    if (!editable || busy) return;
    setLocalError(null);
    const next = (proposal.items ?? [])
      .map((i) => (i.item_id === itemId ? { ...i, selected } : i))
      .filter((i) => i.selected !== false)
      .map((i) => i.item_id);
    try {
      await patchMut.mutateAsync({
        threadId,
        proposalId,
        selected_item_ids: next,
      });
      await refetch();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleConfirm = async () => {
    setLocalError(null);
    try {
      await confirmMut.mutateAsync({ threadId, proposalId });
      await refetch();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Confirm failed');
    }
  };

  const handleReject = async () => {
    setLocalError(null);
    try {
      await rejectMut.mutateAsync({ threadId, proposalId });
      await refetch();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Reject failed');
    }
  };

  const handleUndo = async () => {
    setLocalError(null);
    try {
      const child = await undoMut.mutateAsync({ threadId, proposalId });
      onOpenProposal?.(child);
      await refetch();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Undo failed');
    }
  };

  const callout = impactCallout(proposal);

  return (
    <div className="mt-2 w-full max-w-2xl rounded-xl border border-border bg-surface shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{proposal.title}</p>
            {proposal.summary ? (
              <p className="mt-1 text-xs text-foreground-muted">{proposal.summary}</p>
            ) : null}
          </div>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              proposal.status === 'succeeded' && 'bg-emerald-500/10 text-emerald-700',
              (proposal.status === 'draft' || proposal.status === 'undo_draft') &&
                'bg-amber-500/10 text-amber-800',
              proposal.status === 'failed' && 'bg-error-subtle text-error-ink',
              proposal.status === 'rejected' && 'bg-surface-muted text-foreground-muted',
              proposal.status === 'partial' && 'bg-amber-500/10 text-amber-800',
              proposal.status === 'undone' && 'bg-surface-muted text-foreground-muted'
            )}
          >
            {proposal.status.replace('_', ' ')}
          </span>
        </div>
        <p className="mt-2 text-[11px] text-foreground-muted">
          {selectedCount} selected · Nothing is sent to Amazon until you confirm
        </p>
        {callout ? (
          <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-900">
            {callout}
          </p>
        ) : null}
        {proposal.result &&
        typeof proposal.result === 'object' &&
        'partial_undo_notice' in (proposal.result as Record<string, unknown>) ? (
          <p className="mt-2 text-[11px] text-foreground-muted">
            {String((proposal.result as Record<string, unknown>).partial_undo_notice)}
          </p>
        ) : null}
      </div>

      <div className="max-h-56 overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-surface-muted/80 text-foreground-muted">
            <tr>
              {editable ? <th className="px-3 py-2 font-medium"> </th> : null}
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">Current</th>
              <th className="px-3 py-2 font-medium">Proposed</th>
            </tr>
          </thead>
          <tbody>
            {(proposal.items ?? []).map((item) => (
              <tr key={item.item_id} className="border-t border-border/60">
                {editable ? (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={item.selected !== false}
                      disabled={busy}
                      onChange={(e) => void toggleItem(item.item_id, e.target.checked)}
                    />
                  </td>
                ) : null}
                <td className="px-3 py-2">
                  <div className="font-medium text-foreground">{item.display_name}</div>
                  <div className="text-foreground-muted">{item.ad_product}</div>
                </td>
                <td className="px-3 py-2 text-foreground-muted">
                  {formatValue(item.current)}
                </td>
                <td className="px-3 py-2 text-foreground">{formatValue(item.proposed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {proposal.result ? (
        <div className="border-t border-border px-4 py-2 text-xs text-foreground-muted">
          Result: {proposal.result.succeeded ?? 0} succeeded, {proposal.result.failed ?? 0}{' '}
          failed
          {proposal.error_reason ? ` · ${proposal.error_reason}` : ''}
        </div>
      ) : null}

      {(localError || confirmMut.error || rejectMut.error) && (
        <div className="border-t border-error/20 bg-error-subtle px-4 py-2 text-xs text-error-ink">
          {localError ||
            (confirmMut.error instanceof Error ? confirmMut.error.message : null) ||
            (rejectMut.error instanceof Error ? rejectMut.error.message : null)}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
        {editable ? (
          <>
            <Button size="sm" disabled={busy || selectedCount === 0} onClick={() => void handleConfirm()}>
              {confirmMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Confirm'
              )}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void handleReject()}
            >
              Reject
            </Button>
          </>
        ) : null}
        {proposal.undo_eligible ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void handleUndo()}>
            {undoMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Undo'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
